// components/AdminManagement.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import "./AdminManagement.css";

function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const adminsRef = collection(db, "admins");
      const snapshot = await getDocs(adminsRef);

      const adminList = [];
      snapshot.forEach((doc) => {
        adminList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setAdmins(adminList);
    } catch (err) {
      console.error("Error fetching admins:", err);
      setError("Error al cargar los administradores");
    } finally {
      setLoading(false);
    }
  };

  const searchUsersByEmail = async () => {
    if (!searchInput.trim()) return;

    try {
      setSearchLoading(true);
      setError("");
      setUserSearchResults([]);

      // First, try to find existing user in the users collection
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("email", ">=", searchInput.toLowerCase()),
      );
      const snapshot = await getDocs(q);

      const results = [];
      snapshot.forEach((doc) => {
        const userData = doc.data();
        // Check if email contains the search term
        if (
          userData.email &&
          userData.email.includes(searchInput.toLowerCase())
        ) {
          results.push({
            id: doc.id,
            ...userData,
            isAlreadyAdmin: admins.some(
              (admin) =>
                admin.userId === userData.uid || admin.email === userData.email,
            ),
          });
        }
      });

      setUserSearchResults(results);

      if (results.length === 0) {
        setError("No se encontraron usuarios con ese email");
      }
    } catch (err) {
      console.error("Error searching users:", err);
      setError("Error al buscar usuarios");
    } finally {
      setSearchLoading(false);
    }
  };

  const addAdminByEmail = async () => {
    if (!emailInput.trim()) {
      setError("Por favor ingrese un email");
      return;
    }

    try {
      setError("");
      const email = emailInput.toLowerCase().trim();

      // Check if already an admin
      const adminsRef = collection(db, "admins");
      const q = query(adminsRef, where("email", "==", email));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setError("Este usuario ya es administrador");
        return;
      }

      // Add to admins collection
      const newAdmin = {
        email: email,
        addedAt: new Date().toISOString(),
        addedBy: getAuth().currentUser?.email || "unknown",
      };

      await addDoc(adminsRef, newAdmin);
      setEmailInput("");
      fetchAdmins();
      setError("Administrador agregado exitosamente");
    } catch (err) {
      console.error("Error adding admin:", err);
      setError("Error al agregar administrador");
    }
  };

  const addAdminFromSearch = async (user) => {
    try {
      setError("");

      // Check if already an admin
      if (user.isAlreadyAdmin) {
        setError("Este usuario ya es administrador");
        return;
      }

      const adminsRef = collection(db, "admins");
      const newAdmin = {
        userId: user.uid,
        email: user.email,
        name: user.name || user.displayName || "Usuario",
        addedAt: new Date().toISOString(),
        addedBy: getAuth().currentUser?.email || "unknown",
      };

      await addDoc(adminsRef, newAdmin);
      fetchAdmins();

      // Update search results
      setUserSearchResults((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, isAlreadyAdmin: true } : u,
        ),
      );

      setError("Administrador agregado exitosamente");
    } catch (err) {
      console.error("Error adding admin:", err);
      setError("Error al agregar administrador");
    }
  };

  const removeAdmin = async (adminId, adminEmail) => {
    if (
      !window.confirm(
        `¿Está seguro de eliminar los privilegios de administrador de ${adminEmail}?`,
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "admins", adminId));
      fetchAdmins();
      setError("Administrador eliminado exitosamente");
    } catch (err) {
      console.error("Error removing admin:", err);
      setError("Error al eliminar administrador");
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return dateString;
    }
  };

  return (
    <div className="admin-management">
      <div className="management-header">
        <h1>Gestión de Administradores</h1>
        <p>Agregar o eliminar administradores del sistema</p>
      </div>

      {error && (
        <div
          className={`alert ${error.includes("exitosamente") ? "success" : "error"}`}
        >
          {error}
        </div>
      )}

      {/* Current Admins List */}
      <div className="current-admins-section">
        <div className="section-header">
          <h2>Administradores Actuales ({admins.length})</h2>
          <button onClick={fetchAdmins} className="refresh-btn">
            🔄 Actualizar
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando administradores...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <p>No hay administradores registrados.</p>
          </div>
        ) : (
          <div className="admins-table">
            <div className="table-header">
              <div className="table-cell">Email</div>
              <div className="table-cell">Agregado por</div>
              <div className="table-cell">Fecha de asignación</div>
              <div className="table-cell actions">Acciones</div>
            </div>

            {admins.map((admin) => (
              <div key={admin.id} className="table-row">
                <div className="table-cell email">
                  <div className="admin-email">{admin.email}</div>
                  {admin.name && <div className="admin-name">{admin.name}</div>}
                </div>
                <div className="table-cell added-by">
                  {admin.addedBy || "Sistema"}
                </div>
                <div className="table-cell date-added">
                  {formatDate(admin.addedAt)}
                </div>
                <div className="table-cell actions">
                  <button
                    onClick={() => removeAdmin(admin.id, admin.email)}
                    className="remove-admin-btn"
                  >
                    Quitar permisos
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Super Admin Info */}
      <div className="super-admin-info">
        <h3>Super Administradores</h3>
        <div className="super-admin-list">
          <div className="super-admin-item">
            <span className="super-email">joaquinlen@gmail.com</span>
            <span className="super-badge">Super Admin</span>
          </div>
          <div className="super-admin-item">
            <span className="super-email">esteban@owlstudio.tv</span>
            <span className="super-badge">Super Admin</span>
          </div>
        </div>
        <small className="info-note">
          Los super administradores tienen acceso total al sistema y no pueden
          ser eliminados.
        </small>
      </div>
    </div>
  );
}

export default AdminManagement;
